import { useLoader, useStore } from 'commons/hooks';
import useDisclosure from 'commons/hooks/useDisclosure';
import { ApiPortalFile, ApiSpec } from 'data/schemas/apiSpec';
import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import * as _repository from 'data/repositories/apiSpecs';
import { getMasterEmployee } from 'data/repositories/auth/authentication.repository';
import { AuthenticationResponse } from 'data/schemas/auth';

export type ApiSpecState = ApiSpec & { removeable: boolean; changeable: boolean };

function F1ApiPortalViewModel() {
    const screenLoader = useLoader();
    const { authentication } = useStore();
    const [specs, setSpecs] = useState<ApiSpecState[]>([]);
    const [employee, setEmployee] = useState<AuthenticationResponse[]>([]);
    const [selectedSpec, setSelectedSpec] = useState<ApiSpecState | null>(null);
    const [specFiles, setSpecFiles] = useState<ApiPortalFile[]>([]);
    const detailModal = useDisclosure();

    const fetchListApiSpecs = useCallback(async () => {
        try {
            screenLoader.show();
            return await _repository.listApiSpecs();
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message
                });
            }
        } finally {
            screenLoader.hide();
        }
    }, []);

    const fetchApiSpecById = useCallback(async (id: number) => {
        try {
            screenLoader.show();
            return await _repository.getApiSpecById(id);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message
                });
            }
        } finally {
            screenLoader.hide();
        }
    }, []);

    const onSaveApiSpec = useCallback(async (request: ApiSpec[]) => {
        try {
            screenLoader.show();
            await _repository.saveApiSpec(request);

            const action = await Swal.fire({
                icon: 'success',
                title: 'API spec saved successfully'
            });

            if (action.isConfirmed) {
                loadApiSpecs();
                return;
            }
        } catch (error) {
            if (error instanceof Error) {
                // const response: SaveApiSpecResponse = JSON.parse(error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to save API spec',
                    text: error.message
                });
            }
        } finally {
            screenLoader.hide();
        }
    }, []);

    const loadApiSpecs = useCallback(async () => {
        const response = (await fetchListApiSpecs()) ?? [];

        const data: ApiSpecState[] = response.map(spec => ({
            ...spec,
            removeable: false,
            changeable: false
        }));
        onUpdateSpec(data);
    }, []);

    // fetch specs on mount
    useEffect(() => {
        loadApiSpecs();
        loadMasterEmployee();
    }, []);

    const onUpdateSpec = (specs: ApiSpecState[]) => {
        setSpecs(specs);
    };

    const removeSpec = (index: number) => {
        const newSpecs = [...specs];
        newSpecs.splice(index, 1);
        onUpdateSpec(newSpecs);
    };

    const editSpec = (index: number) => {
        const newSpecs = [...specs];
        newSpecs[index].changeable = true;
        onUpdateSpec(newSpecs);
    };

    const discardChange = async (index: number) => {
        const resetSpec = await fetchApiSpecById(specs[index].id);
        if (!resetSpec) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Spec id: ${specs[index].id} not found`
            });
            return;
        }

        const updatedSpecs = [...specs];
        const indexReset = updatedSpecs.findIndex(s => s.id === resetSpec.id);
        if (indexReset !== -1) {
            updatedSpecs[indexReset] = { ...resetSpec, changeable: false, removeable: false };
        }
        onUpdateSpec(updatedSpecs);
    };

    const handleInputChange = <K extends keyof ApiSpec>(index: number, field: K, value: string) => {
        const newSpecs = [...specs];
        newSpecs[index][field as string] = value;
        console.log(newSpecs);
        onUpdateSpec(newSpecs);
    };

    const addApiSpec = () => {
        const newSpec: ApiSpecState = {
            id: 0,
            api_group: '',
            api_name: '',
            module: '',
            api_namespace: '',
            classname: '',
            function_name: '',
            owner: '',
            updated_by: '',
            updated_on: '',
            remark: '',
            status: 'active',
            is_internal: true,
            removeable: true,
            changeable: true
        };
        onUpdateSpec([...specs, newSpec]);
    };

    const handleAction = <K extends 'remove' | 'edit' | 'discard'>(key: K, index: number) => {
        switch (key) {
            case 'remove':
                removeSpec(index);
                break;
            case 'edit':
                editSpec(index);
                break;
            case 'discard':
                discardChange(index);
                break;
        }
    };

    const saveApiSpec = () => {
        const unsavedSpecs = specs.filter(spec => spec.changeable);

        if (unsavedSpecs.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'No Changes',
                text: 'There are no changes to save.'
            });
            return;
        }

        const request: ApiSpec[] = unsavedSpecs.map(spec => ({
            id: spec.id,
            api_group: spec.api_group,
            api_name: spec.api_name,
            module: spec.module,
            api_namespace: spec.api_namespace,
            classname: spec.classname,
            function_name: spec.function_name,
            owner: spec.owner,
            status: spec.status,
            is_internal: true,
            updated_by: authentication.getCitizen ?? 'Unknown',
            remark: spec.remark
        }));
        onSaveApiSpec(request);
    };

    const loadMasterEmployee = useCallback(async () => {
        const response = await getMasterEmployee({ citizenId: '' });

        setEmployee(response);
    }, []);

    const openDetail = async (index: number) => {
        try {
            const spec = specs[index];
            setSelectedSpec(spec);
            const files = await _repository.getApiSpecFiles(spec.api_group, spec.api_name);
            setSpecFiles(files);

            console.log(specFiles);
            console.log(files);
            detailModal.onOpen();
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire({ icon: 'error', title: 'Upload failed', text: error.message });
            }
        }
    };

    const onUploadApiSpecFiles = useCallback(
        async (files: File[]) => {
            if (!selectedSpec) return;
            try {
                screenLoader.show();
                await _repository.uploadApiSpecFiles(
                    files,
                    selectedSpec.api_group,
                    selectedSpec.api_name,
                    authentication.getCitizen ?? 'Unknown'
                );
                Swal.fire({ icon: 'success', title: 'Files uploaded successfully' });
                const refreshed = await _repository.getApiSpecFiles(
                    selectedSpec.api_group,
                    selectedSpec.api_name
                );
                setSpecFiles(refreshed);
            } catch (error) {
                if (error instanceof Error) {
                    Swal.fire({ icon: 'error', title: 'Upload failed', text: error.message });
                }
            } finally {
                screenLoader.hide();
            }
        },
        [selectedSpec]
    );

    return {
        specs,
        employee,
        selectedSpec,
        specFiles,
        detailModal,
        addApiSpec,
        saveApiSpec,
        handleAction,
        handleInputChange,
        openDetail,
        onUploadApiSpecFiles
    };
}

export default F1ApiPortalViewModel;
